/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import toNumber from 'lodash/toNumber';

import {
    Icon,
    SecurityHelper,
    DatePicker,
    Select,
    Stack,
    Inline,
    Button,
    localToday,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';

import { getGatewayUrl } from 'shared-ui/utils/getGatewayUrl';

import { gnoment, gnomentToCalendarDate, calendarDateToGnoment } from 'shared-ui/gnoment';
import DetailedAvailabilityPageMain from 'ui-permit/shared-components/detailed-availability/DetailedAvailabilityPageMain';
import DetailedAvailabilityPageLayout, {
    LayoutControlContext,
} from 'ui-permit/shared-components/detailed-availability/DetailedAvailabilityPageLayout';
import DetailedAvailabilityFilter from 'ui-permit/shared-components/detailed-availability/DetailedAvailabilityFilter';
import DetailedAvailabilityPageSide from 'ui-permit/shared-components/detailed-availability/DetailedAvailabilityPageSide';
import DetailedAvailabilityGridAndActions from 'ui-permit/shared-components/detailed-availability/DetailedAvailabilityGridAndActions';
import { shouldUseSmallerGrid } from 'ui-permit/shared-components/utils/layoutUtils';
import DetailedAvailabilityFilterItem from 'ui-permit/shared-components/detailed-availability/DetailedAvailabilityFilterItem';
import DivisionsMapContainer from 'ui-permit/shared-components/venuePage/DivisionsMapContainer';
import {
    loadPermitMapping,
    permitContentSelector,
} from 'ui-permit/shared-components/slices/permitContent';

import DivisionDetailsModal from 'ui-permit/shared-components/division-details/DivisionDetailsModal';
import * as types from '../constants';
import * as pActions from '../actions/permit';
import { hasAccessToPermit } from '../utils/availabilityUtils';

import AvailabilityCanadaMotorContainer from './AvailabilityCanadaMotorContainer';
import AvailabilityCanadaPaddleContainer from './AvailabilityCanadaPaddleContainer';
import AvailabilityDayUseMotorContainer from './AvailabilityDayUseMotorContainer';
import AvailabilityOvernightHikeContainer from './AvailabilityOvernightHikeContainer';
import AvailabilityOvernightMotorContainer from './AvailabilityOvernightMotorContainer';
import AvailabilityOvernightPaddleContainer from './AvailabilityOvernightPaddleContainer';
import DivisionFilters from '../components/DivisionFilters';
import ErrorNotification from '../components/ErrorNotification';
import UnauthorizedErrorModal from '../components/UnauthorizedErrorModal';
import InformationRequired from '../components/InformationRequired';

const permitTypeOptions = [
    {
        label: 'Overnight Paddle',
        value: types.TYPE_OVERNIGHT_PADDLE,
    },
    {
        label: 'Overnight Hike',
        value: types.TYPE_OVERNIGHT_HIKE,
    },
    {
        label: 'Overnight Motor',
        value: types.TYPE_OVERNIGHT_MOTOR,
    },
    {
        label: 'Day Use Motor',
        value: types.TYPE_DAYUSE_MOTOR,
    },
    {
        label: 'Motor trips to/from Canada',
        value: types.TYPE_CANADA_MOTOR,
    },
    {
        label: 'Paddle to/from Canada',
        value: types.TYPE_CANADA_PADDLE,
    },
];

const availableStartDate = `${gnoment().year()}-05-01`;

export class AvailabilityPage extends React.Component {
    static displayName = 'AvailabilityPage';

    static propTypes = {
        router: COMPAT_ROUTER_PROP_TYPES,
        dispatch: PropTypes.func,
        divisionSearchCriteria: PropTypes.object,
        permit: PropTypes.object.isRequired,
        permitDate: PropTypes.string,
        permitType: PropTypes.string,
        selectedStartDate: PropTypes.string,
        selectedStartDate2: PropTypes.string,
        questionnaire: PropTypes.shape({
            motorSpendNightOnWayToCanada: PropTypes.string,
            motorSpendNightOnWayFromCanada: PropTypes.string,
        }),
        divisionDetailsModal: PropTypes.shape({
            isOpen: PropTypes.bool,
            divisionId: PropTypes.string,
        }),
        permitMapping: PropTypes.shape({}),
        issuance: PropTypes.any,
        issuanceError: PropTypes.any,
    };

    constructor(props) {
        super(props);

        this.state = {
            permitId: types.BOUNDARY_WATERS_PERMIT_ID,
            showFilters: false,
            showUnauthorizedModal: false,
            entryDatePickerValue: null,
        };

        this.handleDateChange = this.handleDateChange.bind(this);
        this.handlePermitTypeChange = this.handlePermitTypeChange.bind(this);
        this.onFiltersApply = this.onFiltersApply.bind(this);
        this.onFiltersClear = this.onFiltersClear.bind(this);
        this.onFiltersClose = this.onFiltersClose.bind(this);
        this.onEntryDateChange = this.onEntryDateChange.bind(this);
        this.closeUnauthorizedModal = this.closeUnauthorizedModal.bind(this);
    }

    componentDidMount() {
        const permitId = this.state.permitId;

        this.props.dispatch(pActions.fetchPermit(permitId));

        this.props.dispatch(loadPermitMapping());

        // parse url parameters and initialize container
        const permitType = this.props.router.searchParams.get('type');
        const permitDate = this.props.router.searchParams.get('date');
        const activeTab = this.props.router.searchParams.get('tab');
        const existingReservationId = this.props.router.searchParams.get('reservationId');

        if (
            permitType === types.TYPE_OVERNIGHT_PADDLE ||
            permitType === types.TYPE_OVERNIGHT_HIKE ||
            permitType === types.TYPE_OVERNIGHT_MOTOR ||
            permitType === types.TYPE_DAYUSE_MOTOR ||
            permitType === types.TYPE_CANADA_MOTOR ||
            permitType === types.TYPE_CANADA_PADDLE
        ) {
            this.props.dispatch(pActions.setPermitType(permitType));
        }

        if (gnoment(this.props.permitDate).isBefore(availableStartDate)) {
            this.handleDateChange(gnoment(availableStartDate));
        }

        if (gnoment(permitDate).isValid()) {
            this.handleDateChange(gnoment(permitDate));
        }

        if (activeTab === '0' || activeTab === '1') {
            this.props.dispatch(pActions.setActiveTab(toNumber(activeTab)));
        }

        if (existingReservationId) {
            this.fetchExistingReservation(existingReservationId);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!hasAccessToPermit(SecurityHelper.getAccount(), this.props.permit.id)) {
            this.setState({ showUnauthorizedModal: true });
        }
        if (
            nextProps.permitDate !== this.props.permitDate ||
            nextProps.permitType !== this.props.permitType
        ) {
            window.history.replaceState(
                {},
                '',
                `?type=${nextProps.permitType}&date=${nextProps.permitDate}`
            );
        }
    }

    componentWillUnmount() {
        this.props.dispatch(pActions.clearIssuanceErrors());
        this.props.dispatch(pActions.clearExistingIssuance());
    }

    // date is a gnoment utc
    handleDateChange(date) {
        this.props.dispatch(pActions.setPermitDate(date.toISOString()));
        this.setState({ entryDatePickerValue: gnomentToCalendarDate(date) });
    }

    handlePermitTypeChange(e) {
        this.onFiltersClear();
        const value = e.target.value;
        this.props.dispatch(pActions.setPermitType(value));
        this.props.dispatch(pActions.setSelectedDivision(null));
        this.props.dispatch(pActions.setSelectedDivision2(null));
    }

    onEntryDateChange(date, validation) {
        if (date !== null && !validation.isInvalid) {
            this.props.dispatch(
                pActions.setPermitDate(
                    calendarDateToGnoment(date).utc().startOf('day').toISOString()
                )
            );
        }

        this.setState({ entryDatePickerValue: date });
    }

    onFiltersApply(searchText, areas, motorSize, canadaAccess) {
        this.setState({
            showFilters: false,
        });
        this.props.dispatch(
            pActions.setDivisionSearchCriteria({
                areas,
                motorSize,
                searchText,
                canadaAccess,
            })
        );
    }

    onFiltersClear() {
        this.props.dispatch(
            pActions.setDivisionSearchCriteria({
                areas: [],
                motorSize: null,
                searchText: '',
                canadaAccess: null,
            })
        );
    }

    onFiltersClose() {
        this.setState({
            showFilters: false,
        });
    }

    fetchExistingReservation = (existingReservationId) => {
        // load existing issuance
        const issuanceId = existingReservationId.split(types.RES_ID_DELIMITER)[0];
        const permitId = existingReservationId.split(types.RES_ID_DELIMITER)[1];
        this.props
            .dispatch(pActions.fetchUnpaidIssuance(issuanceId, permitId))
            .then(() => {
                if (
                    !this.props.issuanceError &&
                    this.props.issuance.permit_type !== types.TYPE_DAYUSE_MOTOR
                ) {
                    this.props.dispatch(
                        pActions.setExistingIssuance(this.props.issuance)
                    );
                }
            });
    };

    closeDivisionDetailsModal = () => {
        this.props.dispatch(pActions.closeDivisionDetailsModal());
    };

    closeUnauthorizedModal() {
        this.setState({ showUnauthorizedModal: false });
    }

    renderUnauthModal = () => {
        if (!this.state.showUnauthorizedModal) return null;

        return (
            <UnauthorizedErrorModal
                isOpen={this.state.showUnauthorizedModal}
                closeModal={this.closeUnauthorizedModal}
            />
        );
    };

    renderSide = () => {
        return (
            <DetailedAvailabilityPageSide>
                <div className="per-availability-map" id="map">
                    <DivisionsMapContainer
                        permit={this.props.permit}
                        permitType="boundarywaters"
                    />
                </div>
            </DetailedAvailabilityPageSide>
        );
    };

    renderErrorNotification = () => {
        const { questionnaire, selectedStartDate, selectedStartDate2 } = this.props;

        const oneOvernightPermitNecessaryErr =
            questionnaire.motorSpendNightOnWayToCanada === 'yes' &&
            questionnaire.motorSpendNightOnWayFromCanada === 'yes' &&
            selectedStartDate &&
            selectedStartDate2
                ? 'You only need one permit for an overnight permit when spending the night in the BWCAW. Read the instruction to determine which one you need.'
                : '';

        return (
            <div className="per-availability-title">
                <ErrorNotification
                    className="bottom"
                    errors={[oneOvernightPermitNecessaryErr]}
                    noHeader
                />
            </div>
        );
    };

    renderAvailabilityFilter() {
        const { permitType, divisionSearchCriteria } = this.props;
        let filtersCount = 0;

        if (
            divisionSearchCriteria.searchText &&
            divisionSearchCriteria.searchText !== ''
        ) {
            filtersCount += 1;
        }

        if (divisionSearchCriteria.areas && divisionSearchCriteria.areas.length > 0) {
            filtersCount += divisionSearchCriteria.areas.length;
        }

        if (divisionSearchCriteria.motorSize !== null) {
            filtersCount += 1;
        }

        return (
            <Stack>
                <Inline alignY="top">
                    <DetailedAvailabilityFilterItem
                        id="select-quota"
                        mobileLabel="Select Permit Type"
                    >
                        <Select
                            id="permit-type-dropdown"
                            label="Permit Type:"
                            isLabelVisible={false}
                            value={permitType || ''}
                            onChange={this.handlePermitTypeChange}
                            placeholder="Select Permit Type"
                            options={permitTypeOptions}
                        />
                    </DetailedAvailabilityFilterItem>

                    <DetailedAvailabilityFilterItem
                        id="entry-date"
                        mobileLabel="Select an entry date"
                    >
                        <DatePicker
                            label="Select an Entry Date"
                            isLabelVisible={false}
                            minValue={localToday}
                            value={this.state.entryDatePickerValue}
                            onChange={this.onEntryDateChange}
                            showClearDates={false}
                        />
                    </DetailedAvailabilityFilterItem>

                    <DetailedAvailabilityFilterItem id="filer-button">
                        <Button
                            appearance="tertiary"
                            iconBeforeElement={<Icon iconName="filter-list" />}
                            onClick={() => {
                                this.setState((prev) => {
                                    return {
                                        showFilters: !prev.showFilters,
                                    };
                                });
                            }}
                        >
                            {filtersCount === 0 && <span>Filters</span>}{' '}
                            {filtersCount > 0 && (
                                <span>
                                    <strong>{filtersCount} Filter(s)</strong>
                                </span>
                            )}
                        </Button>
                    </DetailedAvailabilityFilterItem>
                </Inline>
                {this.state.showFilters && (
                    <DetailedAvailabilityFilterItem id="filer-panel">
                        <DivisionFilters
                            searchCriteria={divisionSearchCriteria}
                            onApply={this.onFiltersApply}
                            onClear={this.onFiltersClear}
                            onClose={this.onFiltersClose}
                        />
                    </DetailedAvailabilityFilterItem>
                )}
            </Stack>
        );
    }

    renderGrid = (isSmallerGrid, isFullView) => {
        const { permitType } = this.props;

        const daysToSkip = isSmallerGrid
            ? types.AVAILABILITY_DAYS_TO_SKIP_MOBILE
            : types.AVAILABILITY_DAYS_TO_SKIP;
        const numOfCols = isSmallerGrid
            ? types.AVAILABILITY_DAYS_TO_DISPLAY_MOBILE
            : types.AVAILABILITY_DAYS_TO_DISPLAY;

        let grid = null;

        if (permitType === types.TYPE_OVERNIGHT_PADDLE) {
            grid = (
                <AvailabilityOvernightPaddleContainer
                    isSmallerGrid={isSmallerGrid}
                    daysToSkip={daysToSkip}
                    numOfCols={numOfCols}
                    isFullView={isFullView}
                    handleDateChange={this.handleDateChange}
                />
            );
        } else if (permitType === types.TYPE_OVERNIGHT_HIKE) {
            grid = (
                <AvailabilityOvernightHikeContainer
                    isSmallerGrid={isSmallerGrid}
                    daysToSkip={daysToSkip}
                    numOfCols={numOfCols}
                    isFullView={isFullView}
                    handleDateChange={this.handleDateChange}
                />
            );
        } else if (permitType === types.TYPE_OVERNIGHT_MOTOR) {
            grid = (
                <AvailabilityOvernightMotorContainer
                    isSmallerGrid={isSmallerGrid}
                    daysToSkip={daysToSkip}
                    numOfCols={numOfCols}
                    isFullView={isFullView}
                    handleDateChange={this.handleDateChange}
                />
            );
        } else if (permitType === types.TYPE_DAYUSE_MOTOR) {
            grid = (
                <AvailabilityDayUseMotorContainer
                    isSmallerGrid={isSmallerGrid}
                    daysToSkip={daysToSkip}
                    numOfCols={numOfCols}
                    isFullView={isFullView}
                    handleDateChange={this.handleDateChange}
                />
            );
        } else if (permitType === types.TYPE_CANADA_MOTOR) {
            grid = (
                <AvailabilityCanadaMotorContainer
                    isSmallerGrid={isSmallerGrid}
                    daysToSkip={daysToSkip}
                    numOfCols={numOfCols}
                    isFullView={isFullView}
                    handleDateChange={this.handleDateChange}
                />
            );
        } else if (permitType === types.TYPE_CANADA_PADDLE) {
            grid = (
                <AvailabilityCanadaPaddleContainer
                    isSmallerGrid={isSmallerGrid}
                    daysToSkip={daysToSkip}
                    numOfCols={numOfCols}
                    isFullView={isFullView}
                    handleDateChange={this.handleDateChange}
                />
            );
        } else {
            grid = <InformationRequired divisionType={'sites'} hasTypeRequirement />;
        }

        return grid;
    };

    renderCalendarSection() {
        return (
            <DetailedAvailabilityGridAndActions>
                <LayoutControlContext.Consumer>
                    {(contextValue) => {
                        const { isFullView } = contextValue;
                        const isSmallerGrid = shouldUseSmallerGrid(contextValue);
                        return this.renderGrid(isSmallerGrid, isFullView);
                    }}
                </LayoutControlContext.Consumer>
            </DetailedAvailabilityGridAndActions>
        );
    }

    renderMain() {
        const path = [
            { title: 'Home', url: '/' },
            { title: 'Superior National Forest', url: getGatewayUrl('1085') },
            { title: 'Boundary Waters Canoe Area Wilderness', url: '/permits/233396' },
            { title: 'Detailed Availability' },
        ];
        return (
            <DetailedAvailabilityPageMain breadcrumbPath={path}>
                <DetailedAvailabilityFilter>
                    {this.renderAvailabilityFilter()}
                </DetailedAvailabilityFilter>

                {this.renderCalendarSection()}
            </DetailedAvailabilityPageMain>
        );
    }

    render() {
        return (
            <DetailedAvailabilityPageLayout permitName="Boundary Waters Canoe Area Wilderness">
                <DivisionDetailsModal
                    permit={this.props.permit}
                    hasMap
                    divisionId={this.props.divisionDetailsModal.divisionId}
                    isOpen={this.props.divisionDetailsModal.isOpen}
                    onRequestClose={this.closeDivisionDetailsModal}
                    permitMapping={this.props.permitMapping}
                />
                {this.renderErrorNotification()}
                {this.renderMain()}
                {this.renderSide()}
                {this.renderUnauthModal()}
            </DetailedAvailabilityPageLayout>
        );
    }
}

export default withCompatRouter(
    connect((state) => {
        return {
            questionnaire: state.boundarywaters.questionnaire,
            selectedStartDate: state.boundarywaters.selectedStartDate,
            selectedStartDate2: state.boundarywaters.selectedStartDate2,
            divisionSearchCriteria: state.boundarywaters.divisionSearchCriteria,
            permit: state.boundarywaters.permit.result,
            permitDate: state.boundarywaters.permitDate,
            permitType: state.boundarywaters.permitType,
            issuance: state.boundarywaters.permit.issuance,
            issuanceError: state.boundarywaters.permit.fetch_issuance_error,
            divisionDetailsModal: state.boundarywaters.divisionDetailsModal,
            permitMapping: permitContentSelector.permitMapping(state),
        };
    })(AvailabilityPage)
);

// cSpell:ignore boundarywaters, dayuse, BWCAW
